import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Input } from '@/common/components/Input.tsx'
import { AuthLayout } from '../../layout.tsx'
import { AppRoutePaths } from '../../../../../routing/route_paths.ts'
import { Button } from '@/common/components/Button.tsx'
import { z } from 'zod'
import { useAppAlert } from '@/app/AppAlertProvider.tsx'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import FormErrorMessage from '@/app/common/components/FormErrorMessage.tsx'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/storage/store.ts'
import {
  POSTResetPasswordLink,
  resetRequestState,
  setEmail,
} from '@/app-storage/user/resetPasswordEmailSlice.ts'
import { REQUEST_STATUS } from '@/app/common/types_and_interfaces.ts'
import { CircleLoader } from '@/app/common/components/CircleLoader.tsx'
import { useTranslation } from 'react-i18next'
import { PLACEHOLDER_EMAIL } from '@/app/common/constants.ts'

export default function EnterEmailPage() {
  const { t: translate } = useTranslation()

  return (
    <AuthLayout>
      <AuthLayout.Content className="space-y-5" aria-labelledby="forgot-password-heading">
        <div className="text-center">
          <h1 className="text-lg font-semibold text-gray-900" id="forgot-password-heading">
            {translate('forgot_password')}
          </h1>
          <span className="text-sm text-gray-500 font-medium">
            {translate('enter_your_email_to_reset_your_password')}
          </span>
        </div>
        <EnterEmailForm />
      </AuthLayout.Content>
    </AuthLayout>
  )
}

const EnterEmailForm = () => {
  const { t: translate } = useTranslation()
  const navigate = useNavigate()
  const ResetPasswordEmailBase = useSelector(
    (state: RootState) => state.ResetPasswordEmail
  )
  const ResetPasswordEmailState = ResetPasswordEmailBase.request
  const asyncDispatch = useDispatch<AppDispatch>()
  const { fireAlert } = useAppAlert()
  const EnterEmailFormSchema = z.object({
    email: z.string().email({ message: 'Please enter a valid email address' }),
  })
  type EnterEmailFormT = z.infer<typeof EnterEmailFormSchema>
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EnterEmailFormT>({
    resolver: zodResolver(EnterEmailFormSchema),
  })

  const onSubmit = (EmailForm: EnterEmailFormT) => {
    asyncDispatch(setEmail(EmailForm.email))
    asyncDispatch(POSTResetPasswordLink(EmailForm))
  }
  useEffect(() => {
    if (ResetPasswordEmailState.status == REQUEST_STATUS.FAILED) {
      fireAlert({
        variant: 'error',
        message: ResetPasswordEmailState.error.message,
      }).then()
      asyncDispatch(resetRequestState())
      return
    }
    if (ResetPasswordEmailState.status === REQUEST_STATUS.SUCCESS) {
      asyncDispatch(resetRequestState())
      navigate(AppRoutePaths.auth.forgot_password.check_email)
      return
    }
  }, [ResetPasswordEmailState.status])

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          {translate('email')}
        </label>
        <div className="mt-2">
          <Input
            {...register('email')}
            id="email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder={PLACEHOLDER_EMAIL}
          />
          <FormErrorMessage message={errors.email?.message} />
        </div>
      </div>

      <div className="flex">
        <Button type="submit" className="grow" aria-label={translate("reset_password")}>
          {ResetPasswordEmailState.status == REQUEST_STATUS.IN_FLIGHT && (
            <CircleLoader size="sm" className="mr-1" />
          )}
          <span>
            {ResetPasswordEmailState.status == REQUEST_STATUS.IN_FLIGHT
              ? ` ${translate('processing')}...`
              : translate('continue')}
          </span>
        </Button>
      </div>
    </form>
  )
}
